/**
 * RepairRequestsWidget — pixel-matched to the Penpot "Repair Requests"
 * mockup. Horizontal bar per status, bar widths normalized across rows
 * so Completed (the dominant bucket) sets the scale and smaller buckets
 * stay proportional. Single-color bars — no Status/Priority toggle (the
 * Penpot design dropped it).
 *
 * Status order is fixed per the Penpot mockup: Open / In Progress /
 * Awaiting Parts / Completed / Cancelled. Counts come from the BE
 * /repair-requests endpoint (byStatus). DB statuses are free-text
 * varchars in eservbusiness.repair_request — we map common spellings
 * (case + underscores) into the 5 canonical rows.
 */

import * as React from "react";
import { useQueryClient } from "react-query";
import {
  ChartBarIcon,
  RefreshButton,
  StackedBar,
  WidgetCard,
} from "components/dashboard";
import type { StackedBarRow } from "components/dashboard";
import { useRepairRequests, formatQueryError } from "../hooks";
import type { LayoutItem, WidgetConfig } from "../types";
import { bucketRepairStatus, type RepairStatusKey } from "./repairStatusBuckets";

interface Props {
  widget: WidgetConfig;
  layout: LayoutItem;
}

type StatusKey = RepairStatusKey;

const STATUS_ORDER: StatusKey[] = [
  "open",
  "in_progress",
  "awaiting_parts",
  "completed",
  "cancelled",
];

const STATUS_LABELS: Record<StatusKey, string> = {
  open: "Open",
  in_progress: "In Progress",
  awaiting_parts: "Awaiting Parts",
  completed: "Completed",
  cancelled: "Cancelled",
};

// Penpot palette — exact #s from the mockup.
const STATUS_COLORS: Record<StatusKey, string> = {
  open: "250,204,21",         // #facc15 yellow-400
  in_progress: "245,158,11",  // #f59e0b amber-500
  awaiting_parts: "139,92,246", // #8b5cf6 violet-500
  completed: "34,197,94",     // #22c55e green-500
  cancelled: "239,68,68",     // #ef4444 red-500
};

export function RepairRequestsWidget({ widget, layout }: Props) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useRepairRequests();

  // Collapse BE rows into the 5 canonical buckets. Unknown statuses are
  // dropped (BE has free-text values per business; the Penpot mockup
  // locks the visual set to 5).
  const byKey: Record<StatusKey, number> = {
    open: 0,
    in_progress: 0,
    awaiting_parts: 0,
    completed: 0,
    cancelled: 0,
  };
  for (const r of data?.byStatus ?? []) {
    byKey[bucketRepairStatus(r.status)] += r.count;
  }

  // One row per canonical status, in Penpot order. Single segment per row
  // with the status color — StackedBar normalizeAcrossRows scales widths.
  const rows: StackedBarRow[] = STATUS_ORDER.map((k) => ({
    key: k,
    label: STATUS_LABELS[k],
    segments: [
      {
        key: k,
        label: STATUS_LABELS[k],
        value: byKey[k],
        color: STATUS_COLORS[k],
      },
    ],
  }));

  return (
    <WidgetCard
      title={widget.title}
      w={widget.defaultSize.w}
      h={widget.defaultSize.h}
      x={layout.x}
      y={layout.y}
      icon={<ChartBarIcon />}
      loading={isLoading}
      error={error ? formatQueryError(error) : null}
      headerRight={
        <RefreshButton
          onClick={() => queryClient.invalidateQueries(["dashboard", "repair-requests"])}
        />
      }
    >
      <StackedBar
        rows={rows}
        labelWidth={88}
        countWidth={24}
        barHeight={8}
        normalizeAcrossRows
      />
    </WidgetCard>
  );
}
